import { format, parseISO } from "date-fns";

/**
 * Centralized date and time utilities.
 *
 * ── Bare date/time fields ────────────────────────────────────────────────────────
 * Seminar/invoice `datum`/`end_datum`/… are native `date` columns (bare calendar
 * days, "yyyy-MM-dd") and `anfangs_zeit`/`end_zeit` are native `time` columns (bare
 * wall-clock, "HH:mm:ss.SSS"). They carry NO timezone, so every helper here reads
 * their wall-clock components verbatim — the result is timezone-independent by
 * construction. (This replaces the old fixed-offset "civil" convention; the backend
 * half was biteg-api src/domain/civilDateTime.ts, now retired for src/domain/bareDateTime.)
 *   - dates: `formatDateGerman` (dd.MM.yyyy), `toDateValue`/`toISODateString` for the picker.
 *   - times: `formatTime` ("HH:mm"), `toTimeValue`/`toBareTime` for the picker.
 *
 * Real TIMESTAMPS (email sent-at, file dates — genuine instants, NOT these fields)
 * are separate: `formatTimestampDE` / `formatTimestampAtDE`, rendered browser-local.
 */

/**
 * A Luxon DateTime-like object: the non-Date/non-string shapes `toISODateString`
 * accepts. Both methods are optional so the runtime presence checks stay honest.
 */
interface DateTimeLike {
  toISODate?: () => string;
  toISOString?: () => string;
}

/**
 * Format a stored bare value for display.
 *   type "time": a bare "HH:mm:ss.SSS" / "HH:mm" string (or a picked Date) → "HH:mm"
 *   type "date": a bare "yyyy-MM-dd" string (or a Date) → "yyyy-MM-dd"
 * Bare values carry no timezone; their wall-clock components are read verbatim, so
 * the output is identical in every timezone.
 * @param {string|Date} params
 * @param {string} type - "time" or "date"
 * @returns {string} Formatted time or date
 */
export const formatTime = (
  params: string | Date | null | undefined,
  type: string = "time"
): string => {
  if (!params) return "";
  const p = (n: number) => String(n).padStart(2, "0");

  try {
    if (type === "time") {
      if (params instanceof Date) {
        return isNaN(params.getTime()) ? "" : `${p(params.getHours())}:${p(params.getMinutes())}`;
      }
      const bare = /^(\d{1,2}):(\d{2})/.exec(params); // "HH:mm[:ss[.SSS]]"
      if (bare) return `${bare[1].padStart(2, "0")}:${bare[2]}`;
      // Legacy ISO-datetime fallback: read the wall clock in the string, no zone shift.
      const iso = /T(\d{2}):(\d{2})/.exec(params);
      return iso ? `${iso[1]}:${iso[2]}` : "";
    }

    if (type === "date") {
      if (params instanceof Date) {
        return isNaN(params.getTime())
          ? ""
          : `${params.getFullYear()}-${p(params.getMonth() + 1)}-${p(params.getDate())}`;
      }
      const bare = /^(\d{4}-\d{2}-\d{2})/.exec(params); // "yyyy-MM-dd[...]"
      return bare ? bare[1] : "";
    }

    return "";
  } catch (error) {
    console.error("dateUtils.formatTime failed", error);
    return "";
  }
};

/**
 * Format date to German format (dd.MM.yyyy)
 * @param {string} isoDate - ISO date string
 * @returns {string} Formatted date or empty string
 */
export const formatDateGerman = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";

  try {
    const d = parseISO(isoDate);
    if (isNaN(d.getTime())) return "";
    return format(d, "dd.MM.yyyy");
  } catch (error) {
    console.error("dateUtils.formatDateGerman failed", error);
    return "";
  }
};

/**
 * Format a real timestamp (email sent-at, file date — a genuine instant, NOT a
 * civil value) in the browser locale as full date + time. "" for empty input.
 * @param {string} iso - ISO datetime string
 * @returns {string}
 */
export const formatTimestampDE = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("de-DE") : "";

/**
 * Like formatTimestampDE but as "date @ time" (the outbox's sent-at column).
 * @param {string} iso - ISO datetime string
 * @returns {string}
 */
export const formatTimestampAtDE = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("de-DE")} @ ${d.toLocaleTimeString("de-DE")}`;
};

/**
 * Add days to a date
 * @param {Date|string} date - Date object or ISO string
 * @param {number} days - Number of days to add
 * @returns {Date} New date with days added
 */
export const addDays = (date: Date | string, days: number = 0): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Today's local date as a YYYY-MM-DD string, for lexicographic date comparisons
 * (e.g. "is this seminar in the past"). String comparison avoids the timezone
 * pitfalls of comparing parsed timestamps.
 * @returns {string} today as YYYY-MM-DD
 */
export const todayISODate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Coerce a stored bare TIME value into a JS Date for the MUI (date-fns) TimePicker.
 * Accepts a bare "HH:mm[:ss[.SSS]]" string or a Date. The picker renders a Date's
 * LOCAL wall clock, so the stored wall-clock is placed on today's local Date at that
 * hour/minute — no zone conversion. `toBareTime` is the exact inverse on save.
 * @param {string|Date} value
 * @returns {Date|null}
 */
export const toTimeValue = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    // Bare "HH:mm[:ss[.SSS]]" (or the wall clock of a legacy ISO string).
    const hm = /^(\d{1,2}):(\d{2})/.exec(value) || /T(\d{2}):(\d{2})/.exec(value);
    if (hm) {
      const d = new Date();
      d.setHours(Number(hm[1]), Number(hm[2]), 0, 0);
      return d;
    }
    return null;
  }
  return null;
};

/**
 * Serialize a picked time to the bare wall-clock string stored in the native `time`
 * column ("HH:mm:00.000"). The Date's LOCAL wall clock — what the user picked — is
 * read verbatim (no zone conversion). A non-Date value (an untouched stored string)
 * passes through unchanged, so a no-edit save round-trips.
 * @param {string|Date} value
 * @returns {string|null|undefined}
 */
export function toBareTime<T>(value: T | Date): T | string {
  if (value instanceof Date && !isNaN(value.getTime())) {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${p(value.getHours())}:${p(value.getMinutes())}:00.000`;
  }
  return value as T;
}

/**
 * Coerce a stored date value into a JS Date for the MUI (date-fns) DatePicker.
 * Accepts an ISO date/datetime string, a Date, or a falsy value. The TanStack
 * Query cache hands dates back as ISO strings, which the date-fns adapter cannot
 * consume directly — this centralizes the string -> Date coercion that was
 * previously inlined as `new Date(value)` at every picker call site.
 * @param {string|Date} value
 * @returns {Date|null}
 */
export const toDateValue = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    // Bare "yyyy-MM-dd" → LOCAL-midnight Date, so the picker shows that calendar day
    // in every timezone (`new Date("yyyy-MM-dd")` is UTC midnight and slips a day
    // west of UTC).
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Convert Date object to ISO date string (YYYY-MM-DD)
 * Handles Date objects, Luxon DateTime, or already-formatted strings
 * @param {Date|DateTime|string} value - Date value to convert
 * @returns {string} ISO date string
 */
export const toISODateString = (
  value: Date | string | DateTimeLike | null | undefined
): Date | string | DateTimeLike | null | undefined => {
  if (!value) return value;
  if (typeof value === "string") return value;

  try {
    // Handle native Date object. Build from LOCAL components (not toISOString(),
    // which is UTC and shifts the calendar day back one for users east of UTC —
    // editing a seminar date in Germany was saving it one day early). This matches
    // the create path (useSeminarModalState), which already builds local.
    if (value instanceof Date) {
      const p = (n: number) => String(n).padStart(2, "0");
      return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
    }

    // Handle Luxon DateTime
    if (value.toISODate) {
      return value.toISODate();
    }

    // Handle objects with toISOString method
    if (value.toISOString) {
      return value.toISOString().split("T")[0];
    }

    return value;
  } catch (error) {
    console.error("dateUtils.toISODateString failed", error);
    return value;
  }
};
